import * as React from 'react';
import initializeLivePersonaCard from '../actions/initializeLivePersonaCard';
import { observer } from 'mobx-react-lite';
import personaControlStore from '../store/Store';
import { PersonaSize } from 'office-ui-fabric-react/lib/Persona';
import { IStyle } from '@uifabric/styling';
import { isFeatureEnabled } from 'owa-feature-flags';
import ComposeEmailFunction from '../ComposeEmailFunction';
import { calculatePersonaType } from '../utils/calculatePersonaType';

// Keep in sync in interface of the exported component of LPC
interface LPCPersonaRingBehaviorProps {
    personaSize: PersonaSize;
    styles?: {
        root?: IStyle;
        ringContainer?: IStyle;
    };
    identifiers: {
        HostAppPersonaId?: string;
        OlsPersonaId?: string;
        LocationId?: string;
        AadObjectId?: string;
        SatoriId?: string;
        Smtp?: string;
        PersonaType: string;
        PermanentEntryId?: string;
        DisplayName?: string;
    };
    children?: React.ReactNode;
}

interface PersonaRingBehaviorProps extends LPCPersonaRingBehaviorProps {
    isUnauthenticatedSender?: boolean;
    mailBoxType?: string;
    composeEmailOverride?: ComposeEmailFunction;
    displayPersonaHighlightRing?: boolean;
}

const PersonaRingBehavior = observer(function PersonaRingBehaviorWrapper({
    personaSize,
    isUnauthenticatedSender,
    mailBoxType,
    identifiers,
    composeEmailOverride,
    styles,
    children,
}: PersonaRingBehaviorProps) {
    React.useEffect(() => initializeLivePersonaCard(composeEmailOverride), []);

    const calculatedPersonaType = React.useMemo(
        () =>
            calculatePersonaType(
                isUnauthenticatedSender,
                mailBoxType,
                identifiers.PersonaType,
                identifiers.Smtp
            ),
        [isUnauthenticatedSender, mailBoxType, identifiers.PersonaType, identifiers.Smtp]
    );

    const personaTypeIsUser =
        calculatedPersonaType.personaType === 'User' ||
        calculatedPersonaType.personaType === 'NotResolved';
    const PersonaInsightRing = (window as any).PersonaInsightRing as React.ComponentType<
        LPCPersonaRingBehaviorProps
    >;

    if (
        !PersonaInsightRing ||
        !personaControlStore.isLivePersonaCardInitialized ||
        !personaTypeIsUser
    ) {
        return <>{children}</>;
    }

    return (
        <PersonaInsightRing identifiers={identifiers} styles={styles} personaSize={personaSize}>
            {children}
        </PersonaInsightRing>
    );
});

export function usePersonaRingBehavior(props: PersonaRingBehaviorProps): React.ComponentType<{}> {
    return observer(({ children }: { children?: React.ReactNode }) => {
        const personaRingFeatureEnabled = isFeatureEnabled('lpc-isPersonaInsightRingEnabled');

        if (personaRingFeatureEnabled && props.displayPersonaHighlightRing) {
            return <PersonaRingBehavior {...props}>{children}</PersonaRingBehavior>;
        } else {
            return <>{children}</>;
        }
    });
}
